import React, { FormEvent, useEffect, useState, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import AppTurnstile from 'components/app/AppTurnstile';
import AppInputText from 'components/app/AppInputText';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loading from '../common/Loading';
import paths from 'router/paths';
import { api } from 'services/api';
import { useAuth } from 'hooks/useAuth';
import {
    createBanReasonStorage,
    getToken,
    removeBanReasonStorage,
} from 'utils/tokenStorage';
import { BASE_URL, FRESHDESK_REDIRECT_DOMAIN } from 'const/ConstBase';

import 'react-toastify/dist/ReactToastify.css';
import './LoginBody.scss';

// import useSession from 'hooks/useSession';

function initialFormValues() {
    return {
        // dev account
        // email: 'jiiwon0@gmail.com',
        // password: 'lutador1!',
        email: '',
        password: '',
        captcha: '',
    };
}

interface Props {
    //redirect page after login
    redirectTo?: string;
}

type ErrDetails = {
    status: number;
    title: string;
    details: string;
    fieldErrors?: Record<string, string[]>;
};

type freshdeskTokenResponse = {
    res_code: string;
    res_msg: string;
    url: string;
};

const LoginBody: React.FC<Props> = ({ redirectTo }) => {
    const [values, setValues] = useState(initialFormValues);
    const [loginRequestStatus, setLoginRequestStatus] = useState('success');
    const [errors, setErrors] = useState<string[]>([]);

    const { signIn, user, contactLink } = useAuth();
    const location = useLocation();
    const token = getToken();
    const navigate = useNavigate();

    const clientURL =
        'response_type=id_token&client_id=842535401172357186&scope=openid%20email%20profile&state=fwst_fb45df1d43dca9e2d516202491af87c56759dff0d4611975df8dbc8ac1d49368a096541daf9ba752255cb6a43fa6324e2ad2410397d265dcd66aa3e7628c4586da0fb71db37db89613cd1ef506333db18e93ced0eb3dc64e140dc1e86f7f82726b1904d200b125990100f294d820232e&redirect_uri=https://plyosgames.myfreshworks.com/sp/OIDC/842535401172357186/implicit&registration_id=842535401172357186&nonce=vSBiAGR121pgXySk';

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;

        setValues({ ...values, [name]: value });
    }

    const isLoginDisabled = useMemo(() => !values.captcha, [values.captcha]);

    async function fetchFreshdeskToken(clientURL: string, email: string) {
        try {
            const response = await api.get(
                `${BASE_URL}auth/fd_token?${clientURL}&email=${email}`
            );
            const res = response.data;

            if (res && res.res_code === 'SUCCESS') {
                const domain = new URL(res.url);

                console.log('domain', domain);
                console.log(res.url);
                // if (
                //     domain.hostname.toLowerCase() === FRESHDESK_REDIRECT_DOMAIN
                // ) {
                //     window.location.href = res.url;
                // } else {
                //     navigate(paths.ROOT_PATH);
                // }
                navigate(paths.ROOT_PATH);
            } else {
                alert('Fail to sso login. Please try again.');
                navigate(paths.ROOT_PATH);
            }
        } catch (err) {
            alert('Fail to sso login. Please try again.');
            navigate(paths.ROOT_PATH);
        }
    }

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setLoginRequestStatus('loading');
        removeBanReasonStorage();

        try {
            const response = await signIn(values);

            handleAuthResponse(response);
        } catch (error) {
            handleAuthError(error);
        } finally {
            setLoginRequestStatus('success');
        }
    }

    // Handles successful sign-in response
    function handleAuthResponse(response: any) {
        console.log('response', response);

        if (response.status === 'E') {
            navigate(paths.VALID_CODE_PATH);
            return;
        }

        if (response.status === 'V') {
            if (clientURL) {
                //get freshdesk jwt token
                fetchFreshdeskToken(clientURL, values.email);
            } else {
                navigate(redirectTo || paths.PROFILE_PATH);
            }
        }
    }

    // Handles authentication errors
    function handleAuthError(error: any) {
        const data = error.response.data as ErrDetails;

        if (data.details.startsWith('BAN_ACCOUNT')) {
            handleBanAccount(data.details);
            return;
        }

        handleAuthFailure(data);
    }

    // Handles banned account cases
    function handleBanAccount(details: string) {
        const errDetails = details.split('::');
        if (errDetails.length > 1) {
            createBanReasonStorage(errDetails[1]);
        }
        navigate(paths.BAN_PATH);
    }

    // Handles authentication failures
    function handleAuthFailure(data: ErrDetails) {
        switch (data.details) {
            case 'PASS_BLOCK':
                navigate(paths.PASS_BLOCK_PATH);
                break;
            case 'BLOCK_IP':
                navigate(paths.BAN_IP_PATH);
                break;
            case 'BAN_ACCOUNT':
                navigate(paths.BAN_PATH);
                break;
            default:
                processFieldErrors(data.fieldErrors);
                break;
        }
    }

    // Processes form field validation errors
    function processFieldErrors(fieldErrors?: Record<string, string[]>) {
        if (!fieldErrors) return;

        const errorMessages = Object.entries(fieldErrors).flatMap(
            ([key, messages]) =>
                (messages as string[]).map((message) => `${key} ${message}`)
        );

        setErrors(errorMessages);
    }

    useEffect(() => {
        // clean the function to prevent memory leak
        setLoginRequestStatus('success');
    }, []);

    function onCaptchaChange(value: string) {
        setValues((prevValues) => ({
            ...prevValues,
            captcha: value,
        }));
    }

    function onCaptchaError() {
        setValues((prevValues) => ({
            ...prevValues,
            captcha: '',
        }));
    }

    function onCaptchaExpire() {
        setValues((prevValues) => ({
            ...prevValues,
            captcha: '',
        }));
    }

    if (user) {
        if (clientURL) {
            //get freshdesk jwt token
            fetchFreshdeskToken(clientURL, user.email);
        } else {
            navigate(paths.ROOT_PATH);
        }
    }

    return !token ? (
        <div className='sub_content'>
            <ToastContainer />
            <form onSubmit={handleSubmit}>
                <div className='login_box'>
                    <div className='input_container'>
                        <AppInputText
                            label='Email'
                            type='email'
                            id='email'
                            value={values.email}
                            placeholder='Email'
                            disabled={loginRequestStatus === 'loading'}
                            onChange={handleChange}
                            maxLength={40}
                        />
                    </div>
                    <div className='input_container'>
                        <AppInputText
                            label='Senha'
                            type='password'
                            id='password'
                            value={values.password}
                            placeholder='Senha'
                            disabled={loginRequestStatus === 'loading'}
                            onChange={handleChange}
                            maxLength={16}
                        />
                    </div>

                    <div className='error-container'>
                        {errors.length > 0 && (
                            <ul className='error-message-list'>
                                {errors.map((error, index) => (
                                    <li key={index} className='error-message'>
                                        {error}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div>
                        <AppTurnstile
                            id='cf-turnstile-login'
                            onCaptchaChange={onCaptchaChange}
                            onCaptchaError={onCaptchaError}
                            onCaptchaExpire={onCaptchaExpire}
                        />
                    </div>

                    <div className='submit-buttons'>
                        <button
                            type='submit'
                            className={`submit-button login-button ${
                                isLoginDisabled ? 'disabled-button' : ''
                            }`}
                            disabled={isLoginDisabled}
                        >
                            Entrar
                        </button>
                        <a
                            className='submit-button signin-button'
                            href={paths.REGISTER_PATH}
                        >
                            Cadastrar-se
                        </a>
                    </div>

                    <div className='find-password_wrapper'>
                        <a
                            className='find-password'
                            href={paths.FIND_PASSWORD_PATH}
                        >
                            Esqueceu a senha?
                        </a>
                    </div>
                </div>
            </form>
        </div>
    ) : (
        <div className='sub_content'>
            <Loading />
        </div>
    );
};

export default LoginBody;
